import { useSimStore } from './store';
import { SimEvent, ActorState, WorldSnapshot } from './types';

// ═══════════════════════════════════════════════════════════
//  WebSocket → SimStore reducer
//  Call dispatchSimEvent() from useWebSocket's onmessage for
//  any message whose type starts with "sim:". Keep this file
//  as the SINGLE entry point — it's where the wire protocol
//  meets the local state machine.
// ═══════════════════════════════════════════════════════════

export function isSimEvent(msg: any): msg is SimEvent {
  return msg && typeof msg.type === 'string' && msg.type.startsWith('sim:');
}

export function dispatchSimEvent(msg: SimEvent): void {
  const store = useSimStore.getState();

  switch (msg.type) {
    case 'sim:init': {
      const snapshot = msg.data as WorldSnapshot;
      store.hydrate(snapshot);
      break;
    }

    case 'sim:actor': {
      const actor = msg.data as ActorState;
      if (actor?.id) store.setActor(actor);
      break;
    }

    case 'sim:actor-speak': {
      const { actorId, text, ttlMs } = msg.data || {};
      if (actorId && text) store.addBubble(actorId, text, ttlMs);
      break;
    }

    case 'sim:task-progress': {
      const { actorId, progress } = msg.data || {};
      if (actorId && typeof progress === 'number') store.setProgress(actorId, progress);
      break;
    }

    case 'sim:commit': {
      const { actorId, hash, message } = msg.data || {};
      if (actorId) store.addCommit(actorId, hash || '', message || '');
      break;
    }

    case 'sim:phase-progress': {
      // e.g. a module flipped to completed — the scene can flash
      // the corresponding station. Store doesn't need a dedicated
      // list for this yet; bubble works as a transient visual.
      const { actorId, moduleName, status } = msg.data || {};
      if (actorId) store.addBubble(actorId, `${moduleName}: ${status}`, 3000);
      break;
    }

    case 'sim:celebrate': {
      const { actorId, text } = msg.data || {};
      if (actorId) {
        store.setAction(actorId, 'celebrating');
        if (text) store.addBubble(actorId, text, 5000);
      }
      break;
    }

    case 'sim:alert': {
      const { text } = msg.data || {};
      if (text) store.addAlert(text);
      break;
    }

    default:
      // Unknown sim:* event — ignored silently to remain
      // forward-compatible.
      break;
  }
}
